import { H1 } from "../../../../components/text/headings";
import {
  useGetSupplierEngagementReport,
  useGetSupplierOverviewData,
} from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import { YearDropdown } from "../components/FootPrint";
import * as React from "react";
import { useEffect, useState } from "react";
import { Badge, Box, Flex, Grid, GridItem, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { getFormattedNumValue, showErrorToast } from "../../../../utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { routes } from "../../../../config";
import { ArrowLeft } from "@phosphor-icons/react";
import { Page } from "../../../../components/Page";
import DashboardCard from "../../../ISSB/Dashboard/components/DashboardCard";
import BigNumber from "../components/BigNumber";
import { getYoYChange } from "../../../../utils/yoy";
import LocalSpinner from "../../../../components/LocalSpinner";
import IndividualSupplierTrends from "./IndividualSupplierTrends";
import TargetBar from "./TargetBar";
import SupplierFiltersCatQualityYear from "./SupplierFiltersCatQualityYear";
import { useGetSupplierEngagementReports } from "../../../../hooks/query/reports";
import { useGetSupplierQuestionnaireById } from "../../../../hooks/query/supplier-questionnaire/supplier-questionnaire";
import { SUPPLIER_QUESTIONNAIRE_CATEGORIES } from "../../../SupplierQuestionnaire/helpers";
import moment from "moment";
import {
  getStatusLabel
} from "../../SupplierReports/components/SuppliersDataReport/components/SupplierEngagement/components/SupplierEngagementGrid";
import { SuppliersDataModal } from "../../SupplierReports/components/SuppliersDataReport/components";

const SupplierIndividualDetail = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const supplierId = searchParams.get("supplierId");

  const [filters, setFilters] = useState();

  const [currentYearEngagementData, setCurrentYearEngagementData] = useState(null);
  const [previousYearData, setPreviousYearData] = useState(null);

  const [decarbTag, setDecarbTag] = useState("Decarbonisation");
  const [qTypeTag, setQTypeTag] = useState("N.A.");
  const [qStatusTag, setQStatusTag] = useState("N.A.");
  const [qQualityTag, setQQualityTag] = useState("N.A.");
  const [ghgId, setGhgId] = useState(null);
  const [decarbId, setDecarbId] = useState(null);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState(null);

  const {
    isOpen: isSuppliersQuestionnaireOpen,
    onOpen: onSuppliersQuestionnaireOpen,
    onClose: onSuppliersQuestionnaireClose
  } = useDisclosure();

  const prepareData = (data) => {
    const scope1Total = data?.engagementReport?.revenue?.scope1TotalCo2Emissions || 0;
    const scope2Total = data?.engagementReport?.revenue?.scope2TotalCo2Emissions || 0;
    const scope3Total = data?.engagementReport?.revenue?.scope3TotalCo2Emissions || 0;
    const totalCo2Emissions = data?.engagementReport?.totalCo2Emissions || 0;

    const scope1Attributed = data?.engagementReport?.revenue?.scope1ApportionedCo2Emissions || 0;
    const scope2Attributed = data?.engagementReport?.revenue?.scope2ApportionedCo2Emissions || 0;
    const scope3Attributed = data?.engagementReport?.revenue?.scope3ApportionedCo2Emissions || 0;
    // Product and location questionnaires are not apportioned by sourcing %, so
    // the full value counts as attributed to the organisation.
    const productAttributed = data?.engagementReport?.productCo2Emissions || 0;
    const locationAttributed = data?.engagementReport?.locationCo2Emissions || 0;
    const totalAttributed = scope1Attributed + scope2Attributed + scope3Attributed + productAttributed + locationAttributed;
    const year = data?.engagementReport?.year;
    const target = data?.yearlyEmission?.yearlyEmissionData?.[year]?.target || 0;
    let delta = target
      ? ((totalAttributed - target) / target) * 100
      : 0;

    return {
      scope1Total: scope1Total,
      scope2Total: scope2Total,
      scope3Total: scope3Total,
      scope1And2Total: scope1Total + scope2Total,
      scope1Attributed: scope1Attributed,
      scope2Attributed: scope2Attributed,
      scope3Attributed: scope3Attributed,
      scope1And2Attributed: scope1Attributed + scope2Attributed,
      totalCo2Emissions: totalCo2Emissions,
      totalAttributed: totalAttributed,
      supplierName: data?.yearlyEmission?.supplierName || "Unknown Supplier",
      year: year,
      spend: data?.yearlyEmission?.yearlyEmissionData?.[year]?.spend || 0,
      intensity: data?.yearlyEmission?.yearlyEmissionData?.[year]?.intensity || 0,
      target: target,
      delta: delta,
      revenueSpend: data?.yearlyEmission?.yearlyEmissionData?.[year]?.revenueSpend || 0,
    };
  };

  const { isFetching: engagementLoading } = useGetSupplierEngagementReport(
    [filters?.year, filters?.year - 1],
    supplierId,
    {
      onError: (error) => {
        console.error("Error fetching supplier engagement report:", error);
        showErrorToast(toast, "Failed to fetch supplier engagement report", error.message);
      },
      onSuccess: (data) => {
        setCurrentYearEngagementData(prepareData(data?.[filters?.year]));
        setPreviousYearData(prepareData(data?.[filters?.year - 1]));
        setQQualityTag(data?.[filters?.year]?.yearlyEmission?.dataQuality || "N.A.");
      },
      cacheTime: 0,
      staleTime: 0,
      enabled: !!supplierId && !!filters?.year,
    }
  );

  const { data: engagementReport } = useGetSupplierEngagementReports(null, [supplierId], {
    enabled: !!supplierId && !!filters?.year,
    cacheTime: 0,
    staleTime: 0,
  });

  const { data: questionnaireData } = useGetSupplierQuestionnaireById(ghgId, {
    enabled: !!ghgId && qTypeTag === SUPPLIER_QUESTIONNAIRE_CATEGORIES.PRODUCT,
  });
  const productCount = questionnaireData?.products?.length ?? null;

  const closeSuppliersQuestionnaireModal = () => {
    setSelectedQuestionnaireId("");
    onSuppliersQuestionnaireClose();
  };

  const openSuppliersQuestionnaireModal = (questionnaireId) => {
    setSelectedQuestionnaireId(questionnaireId);
    onSuppliersQuestionnaireOpen();
  }

  useEffect(() => {
    const year = filters?.year;

    if (engagementReport && Object.keys(engagementReport)?.length > 0) {
      Object.keys(engagementReport).forEach((supplier) => {
        Object.keys(engagementReport[supplier]).forEach((username) => {
          const foundDecarbQuestionnaire = engagementReport[supplier][username].find(
            (questionnaire) => questionnaire.type === SUPPLIER_QUESTIONNAIRE_CATEGORIES.DECARBONIZATION
          );
          const foundGHGQuestionnaire = engagementReport[supplier][username].find(
            (questionnaire) =>
              questionnaire.type !== SUPPLIER_QUESTIONNAIRE_CATEGORIES.DECARBONIZATION && questionnaire.year === year
          );
          const momentDuration = moment.duration(foundDecarbQuestionnaire?.durationInMinutes, "minutes");
          if (foundDecarbQuestionnaire?.durationInMinutes) {
            setDecarbTag(`Decarbonisation: ${momentDuration.days()}d.`);
          }
          if (foundGHGQuestionnaire?.type) {
            setQTypeTag(foundGHGQuestionnaire.type);
          }
          if (foundGHGQuestionnaire?.status) {
            setQStatusTag(getStatusLabel(foundGHGQuestionnaire.status, null));
          }
          setGhgId(foundGHGQuestionnaire?.questionnaireId);
          setDecarbId(foundDecarbQuestionnaire?.questionnaireId);
        });
      });
    }
  }, [engagementReport, filters?.year]);

  const isProductQuestionnaire = qTypeTag === SUPPLIER_QUESTIONNAIRE_CATEGORIES.PRODUCT;

  return (
    <Page>
      <Flex direction="column" gap={2} mx="10%">
        <Flex onClick={() => navigate(routes.supplierInsightEmissionDetail)} align="center" gap={2} cursor="pointer">
          <ArrowLeft size={22} color="#85C22A" />
          <Text>Back</Text>
        </Flex>

        <H1>{currentYearEngagementData?.supplierName || "Loading"}</H1>

        <Text maxW="75%" color="gray.500" fontSize="body.m">
          Detail of supplier provided data.
        </Text>

        <Flex direction="column" gap={4} mt={8}>
          <Flex alignSelf="end" gap={2}>
            <SupplierFiltersCatQualityYear filters={filters} setFilters={setFilters} showFilters={["year"]} />
          </Flex>

          <Flex gap={2}>
            <Badge bg="lightGreen" color="green.800" p={2} borderRadius="md" colorScheme="limeShade">{qQualityTag}</Badge>
            <Badge bg="status.orange" p={2} borderRadius="md" cursor="pointer" onClick={() => openSuppliersQuestionnaireModal(ghgId)} colorScheme="orange">{qTypeTag}</Badge>
            <Badge bg="status.pink" p={2} borderRadius="md" cursor="pointer" onClick={() => openSuppliersQuestionnaireModal(ghgId)} colorScheme="pink">{qStatusTag}</Badge>
            <Badge bg="limeShade.600" color="white" p={2} borderRadius="md" cursor="pointer" onClick={() => openSuppliersQuestionnaireModal(decarbId)}>{decarbTag}</Badge>
          </Flex>

          <SuppliersDataModal
            questionnaireId={selectedQuestionnaireId}
            isOpen={isSuppliersQuestionnaireOpen}
            onClose={closeSuppliersQuestionnaireModal}
          />

          {/* Column-major layout: cards are placed in reading order so the
              grid columns read — left: Total / Scope 1 & 2 / Target Delta;
              middle: Scope 1 / Scope 2 / Scope 3; right: Spend / Intensity. */}
          <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={6} align="stretch">
            {/* Row 1 */}
            <GridItem h="100%" minH="300px">
              <AttributionCard
                isLoading={engagementLoading}
                title="Total"
                attributed={currentYearEngagementData?.totalAttributed}
                attributedPrev={previousYearData?.totalAttributed}
                supplierTotal={currentYearEngagementData?.totalCo2Emissions}
                showShare={!isProductQuestionnaire}
                productCount={isProductQuestionnaire ? productCount : null}
                onClick={
                  isProductQuestionnaire
                    ? () =>
                        navigate(
                          `${routes.supplierInsightsProductDetail}?supplierId=${supplierId}&questionnaireId=${ghgId}&year=${filters?.year}`
                        )
                    : undefined
                }
              />
            </GridItem>
            {!isProductQuestionnaire && (
              <GridItem h="100%" minH="300px">
                <AttributionCard
                  isLoading={engagementLoading}
                  title="Scope 1"
                  attributed={currentYearEngagementData?.scope1Attributed}
                  attributedPrev={previousYearData?.scope1Attributed}
                  supplierTotal={currentYearEngagementData?.scope1Total}
                />
              </GridItem>
            )}
            <GridItem h="100%" minH="300px">
              <SpendCard
                isLoading={engagementLoading}
                title="Spend"
                value={currentYearEngagementData?.spend}
                previousValue={previousYearData?.spend}
                intensity={currentYearEngagementData?.intensity}
                previousIntensity={previousYearData?.intensity}
              />
            </GridItem>

            {/* Row 2 */}
            {!isProductQuestionnaire && (
              <GridItem h="100%" minH="300px">
                <AttributionCard
                  isLoading={engagementLoading}
                  title="Scope 1 & 2"
                  attributed={currentYearEngagementData?.scope1And2Attributed}
                  attributedPrev={previousYearData?.scope1And2Attributed}
                  supplierTotal={currentYearEngagementData?.scope1And2Total}
                />
              </GridItem>
            )}
            {!isProductQuestionnaire && (
              <GridItem h="100%" minH="300px">
                <AttributionCard
                  isLoading={engagementLoading}
                  title="Scope 2"
                  attributed={currentYearEngagementData?.scope2Attributed}
                  attributedPrev={previousYearData?.scope2Attributed}
                  supplierTotal={currentYearEngagementData?.scope2Total}
                />
              </GridItem>
            )}

            {/* Row 3 */}
            <GridItem h="100%" minH="300px">
              <TargetDeltaCard
                isLoading={engagementLoading}
                title="Target Delta"
                delta={currentYearEngagementData?.delta}
                actual={currentYearEngagementData?.totalAttributed}
                target={currentYearEngagementData?.target}
              />
            </GridItem>
            {!isProductQuestionnaire && (
              <GridItem h="100%" minH="300px">
                <AttributionCard
                  isLoading={engagementLoading}
                  title="Scope 3"
                  attributed={currentYearEngagementData?.scope3Attributed}
                  attributedPrev={previousYearData?.scope3Attributed}
                  supplierTotal={currentYearEngagementData?.scope3Total}
                />
              </GridItem>
            )}
          </Grid>

          <IndividualSupplierTrends supplierId={supplierId} isProductQuestionnaire={isProductQuestionnaire} questionnaireId={ghgId} />
        </Flex>
      </Flex>
    </Page>
  );
};

// Mini donut showing what share of the supplier's whole footprint is
// attributed to the organisation. Purely decorative reinforcement of the %
// (the apportionment ratio is the same across every scope, so we only render
// this on the Total card).
const ShareDonut = ({ share }) => {
  const size = 52;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, share));
  return (
    <Flex align="center" gap={2} flexShrink={0}>
      <Box as="svg" width={`${size}px`} height={`${size}px`} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E2E8F0" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--chakra-colors-lime)"
          strokeWidth={stroke}
          strokeDasharray={`${(pct / 100) * c} ${c}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Box>
      <Box>
        <Text fontSize="body.m" fontWeight="700" color="limeShade.600" lineHeight="1">
          {share.toLocaleString("en-GB", { maximumFractionDigits: 1 })}%
        </Text>
        <Text fontSize="10px" color="gray.500" lineHeight="1.2">
          of supplier total
        </Text>
      </Box>
    </Flex>
  );
};

// Emissions card that leads with the amount ATTRIBUTED to the organisation —
// the number that matters most here — with its YoY chip, then shows the
// whole-supplier footprint as a clearly-labelled secondary figure beneath.
// The Total card additionally shows a mini share donut (showShare) — the
// apportionment ratio is identical across scopes, so we surface it once.
const AttributionCard = ({
  title,
  attributed,
  attributedPrev,
  supplierTotal,
  isLoading,
  showShare = false,
  unit = "tCO2e",
  productCount = null,
  onClick,
}) => {
  // Attributed emissions YoY — a decrease is an improvement (YoYChip down=green).
  const yoy = getYoYChange(attributed, attributedPrev);
  // Share of the supplier's whole footprint attributed to the organisation.
  const share =
    typeof attributed === "number" && typeof supplierTotal === "number" && supplierTotal !== 0
      ? (attributed / supplierTotal) * 100
      : null;

  return (
    <DashboardCard bg="#F7FAFC" px={8} onClick={onClick} cursor={onClick ? "pointer" : undefined}>
      <Text fontSize="heading.l" fontWeight="bold" color="teal">
        {title}
      </Text>
      {isLoading ? (
        <LocalSpinner size="sm" />
      ) : (
        <Flex direction="column" gap={8}>
          <Box>
            <Flex align="center" gap={4} flexWrap="wrap">
              <Box>
                <BigNumber color="darkTeal" pctChange={yoy}>
                  {getFormattedNumValue(attributed, 2, true) || 0}
                </BigNumber>
                <Text fontSize="body.xs" color="gray.500" mt={1}>
                  {unit} attributed to your organisation
                </Text>
                {productCount != null && (
                  <Box mt={4}>
                    <BigNumber color="darkTeal">
                      {productCount}
                    </BigNumber>
                    <Text fontSize="body.xs" color="gray.500" mt={1}>
                      product{productCount !== 1 ? "s" : ""}
                    </Text>
                  </Box>
                )}
              </Box>
              {showShare && share != null && <ShareDonut share={share} />}
            </Flex>

            {showShare && (
              <Box mt={5} pt={4} borderTop="1px solid" borderColor="gray.200">
                <Text fontSize={{ base: "22px", lg: "26px", xl: "30px" }} fontWeight="700" color="gray.700" lineHeight="1">
                  {getFormattedNumValue(supplierTotal, 2, true) || 0}
                </Text>
                <Text fontSize="body.xs" color="gray.500" mt={1}>
                  {unit} · whole supplier footprint
                </Text>
              </Box>
            )}
          </Box>
        </Flex>
      )}
    </DashboardCard>
  );
};

const SpendCard = ({ title, value, previousValue, intensity, previousIntensity, isLoading }) => {
  const yoy = getYoYChange(value, previousValue);
  const intensityYoy = getYoYChange(intensity, previousIntensity);

  return (
    <DashboardCard bg="#F7FAFC" px={8}>
      <Text fontSize="heading.l" fontWeight="bold" color="teal">
        {title}
      </Text>
      {isLoading ? (
        <LocalSpinner size="sm" />
      ) : (
        <Flex direction="column" gap={8}>
          <Box>
            <BigNumber color="gray.700" pctChange={yoy}>
              {getFormattedNumValue(value, 2, true) || 0}
            </BigNumber>
            <Text fontSize="body.xs" color="gray.500" mt={1}>
              (Organization Provided)
            </Text>

            <Box mt={4}>
              <BigNumber color="gray.800" pctChange={intensityYoy}>
                {getFormattedNumValue(intensity, 2, true) || 0}
              </BigNumber>
              <Text fontSize="body.xs" color="gray.500" mt={1}>
                tCO2e/£m · Intensity
              </Text>
            </Box>
          </Box>
        </Flex>
      )}
    </DashboardCard>
  );
};


// Target Delta: the supplier's actual total emissions vs their emissions
// target for the year, as a signed %. Positive = over target (bad, red);
// negative = under target (good, green). Shown as a headline % plus a
// "vs target" bar: a grey track with the target marked at centre, the actual
// drawn as a fill that runs red past the marker (over) or green short of it
// (under). Footer states the actual and target figures in tCO2e.
const TargetDeltaCard = ({ title, delta, actual, target, isLoading }) => {
  const hasDelta = typeof delta === "number" && typeof target === "number" && target !== 0;
  const overTarget = hasDelta && delta > 0;
  const headlineColor = !hasDelta ? "darkTeal" : overTarget ? "red.600" : "green.600";

  const deltaLabel = hasDelta
    ? `${overTarget ? "+" : ""}${delta.toLocaleString("en-GB", { maximumFractionDigits: 1 })} %`
    : "—";

  return (
    <DashboardCard bg="#F7FAFC" px={8}>
      <Text fontSize="heading.l" fontWeight="bold" color="teal">
        {title}
      </Text>
      {isLoading ? (
        <LocalSpinner size="sm" />
      ) : (
        <Flex direction="column" gap={6}>
          <Box>
            <Text
              fontSize={{ base: "28px", lg: "36px", xl: "44px" }}
              fontWeight="700"
              color={headlineColor}
              lineHeight="1"
              letterSpacing="-0.02em"
            >
              {deltaLabel}
            </Text>
            <Text fontSize="body.xs" color="gray.500" mt={1}>
              {!hasDelta ? "vs target" : overTarget ? "over target" : "under target"}
            </Text>

            <Box mt={5}>
              <TargetBar delta={hasDelta ? delta : null} />
            </Box>

            <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
              <Text fontSize="body.s" color="gray.600">
                <Text as="span" fontWeight="700" color="gray.800">
                  {getFormattedNumValue(actual, 2, true) || 0}
                </Text>{" "}
                actual vs{" "}
                <Text as="span" fontWeight="700" color="gray.800">
                  {getFormattedNumValue(target, 2, true) || 0}
                </Text>{" "}
                target · tCO2e
              </Text>
            </Box>
          </Box>
        </Flex>
      )}
    </DashboardCard>
  );
};

export default SupplierIndividualDetail;
